import { Grid } from '@mui/material';
import { useTheme } from '@mui/styles';
import { muiTheme } from '@tassat/payments-themes';
import moment from 'moment';
import * as React from 'react';
import { MonthCaptionProps, useNavigation } from 'react-day-picker';

import { monthStyles, yearStyles } from './styles';
import StyledSelect from '../../StyledSelect';
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE } from '../types';

interface SelectOption {
    label: string;
    value: number;
}

export default function MonthSelector(props: MonthCaptionProps) {
    const { calendarMonth } = props;
    const navigation = useNavigation();
    const { dayPickerProps, goToMonth } = navigation;

    const theme = useTheme<typeof muiTheme>();
    const customMonthStyles = React.useMemo(() => monthStyles(theme), [theme]);
    const customYearStyles = React.useMemo(() => yearStyles(theme), [theme]);

    const selectableYears = React.useMemo((): SelectOption[] => {
        const minDate = moment(dayPickerProps.startMonth);
        const maxDate = moment(dayPickerProps.endMonth);
        if (minDate && maxDate) {
            const maxYear = maxDate.year();
            const oldestYear = minDate.year();

            return Array(maxYear - oldestYear + 1)
                .fill('')
                .map((elem, idx) => ({
                    label: (oldestYear + idx).toString(),
                    value: oldestYear + idx
                }))
                .reverse();
        }

        const today = moment();

        return [
            {
                label: today.year()
                    .toString(),
                value: today.year()
            }
        ];
    }, [ dayPickerProps.startMonth, dayPickerProps.endMonth ]);

    const selectableMonths = React.useMemo((): SelectOption[] => {
        const minDate = moment(dayPickerProps.startMonth);
        const maxDate = moment(dayPickerProps.endMonth);
        const month = moment(calendarMonth.date);
        if (month.year() === minDate.year() &&
            minDate !== DEFAULT_MIN_DATE) {
            const earliestMonth = minDate.month();
            const months = moment
                .months()
                .slice(earliestMonth)
                .map((mon, idx) => ({
                    label: mon,
                    value: idx + earliestMonth
                }));

            return months;
        }

        if (month.year() === maxDate.year() &&
            maxDate !== DEFAULT_MAX_DATE) {
            const lastMonth = maxDate.month();
            const months = moment
                .months()
                .slice(0, lastMonth + 1)
                .map((mon, idx) => ({
                    label: mon,
                    value: idx + lastMonth
                }));

            return months;
        }

        return moment
            .months()
            .map((mon, idx) => ({
                label: mon,
                value: idx
            }));
    }, [ calendarMonth.date, dayPickerProps.startMonth, dayPickerProps.endMonth ]);

    return (
        <Grid
            container
            direction='row'
            justifyContent='center'
            wrap='nowrap'
        >
            <Grid item>
                <StyledSelect
                    customStyles={customMonthStyles}
                    isSearchable={false}
                    maxMenuHeight={200}
                    onChange={(selectedOption) => {
                        if (!selectedOption) return;
                        let newDate;
                        const selectedMonth = moment()
                            .month(selectedOption?.label)
                            .month();
                        if (props.displayIndex === 1) {
                            if (selectedMonth === 0) {
                                newDate = new Date(moment(calendarMonth.date)
                                    .year() - 1, 11);
                            } else {
                                newDate = new Date(moment(calendarMonth.date)
                                    .year(), selectedMonth - 1);
                            }
                        } else {
                            newDate = new Date(moment(calendarMonth.date)
                                .year(), selectedMonth);
                        }
                        goToMonth(newDate);
                    }}
                    options={selectableMonths}
                    value={selectableMonths.find((opt) => (
                        opt.label === moment(calendarMonth.date)
                            .format('MMMM')
                    ))}
                />
            </Grid>
            <Grid item>
                <StyledSelect
                    customStyles={customYearStyles}
                    isSearchable={false}
                    maxMenuHeight={200}
                    onChange={(selectedOption) => {
                        if (!selectedOption) return;
                        let newDate;
                        if (props.displayIndex === 1) {
                            if (moment(calendarMonth.date)
                                .month() === 0) {
                                newDate = new Date(selectedOption.value - 1, 11);
                            } else {
                                newDate = new Date(selectedOption.value, moment(calendarMonth.date)
                                    .month() - 1);
                            }
                        } else {
                            newDate = new Date(selectedOption.value,
                                moment(calendarMonth.date)
                                    .month());
                        }
                        goToMonth(newDate);
                    }}
                    options={selectableYears}
                    value={selectableYears.find((opt) => (
                        opt.value === moment(calendarMonth.date)
                            .year()
                    ))}
                />
            </Grid>
        </Grid>
    );
}